import React, { useMemo, useState, useCallback } from 'react';
import { useAdminSubscriptions } from '../../agents/subscriptionAdminAgent';
import './AllowedEmailsManager.css';
import './SubscriptionAssignmentsManager.css';

function PlanBadges({ codes }) {
  if (!Array.isArray(codes) || codes.length === 0) {
    return <span className="admin-badge admin-badge--none">No active plans</span>;
  }
  return (
    <div className="subscription-badge-list">
      {codes.map((code) => (
        <span key={code} className="admin-badge admin-badge--allowed">{code}</span>
      ))}
    </div>
  );
}

export default function SubscriptionAssignmentsManager() {
  const { plans, users, loading, error, isAdmin, assignSubscription } = useAdminSubscriptions();

  const [busyUserId, setBusyUserId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [search, setSearch] = useState('');
  const [draftPlans, setDraftPlans] = useState({});

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;

    return users.filter((user) => {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
      const email = (user.email || '').toLowerCase();
      const subscriptions = (user.subscription_plans || []).join(' ').toLowerCase();
      return (
        fullName.includes(query)
        || email.includes(query)
        || subscriptions.includes(query)
      );
    });
  }, [users, search]);

  const selectedPlansForUser = useCallback((user) => {
    return draftPlans[user.id] ?? user.subscription_plans ?? [];
  }, [draftPlans]);

  const onTogglePlan = useCallback((userId, planCode, currentlySelected) => {
    setDraftPlans((prev) => {
      const current = new Set(prev[userId] ?? currentlySelected ?? []);
      if (current.has(planCode)) {
        current.delete(planCode);
      } else {
        current.add(planCode);
      }
      return { ...prev, [userId]: [...current].sort() };
    });
  }, []);

  const onAssign = useCallback(async (user) => {
    const selectedPlans = selectedPlansForUser(user);
    if (!Array.isArray(selectedPlans) || selectedPlans.length === 0) {
      setActionError('At least one subscription plan must remain active.');
      return;
    }

    const current = JSON.stringify((user.subscription_plans || []).slice().sort());
    const next = JSON.stringify(selectedPlans.slice().sort());
    if (current === next) return;

    setBusyUserId(user.id);
    setActionError(null);
    try {
      await assignSubscription(user.id, selectedPlans);
      setDraftPlans((prev) => {
        const next = { ...prev };
        delete next[user.id];
        return next;
      });
    } catch (err) {
      setActionError(err?.message || 'Failed to assign subscription');
    } finally {
      setBusyUserId(null);
    }
  }, [assignSubscription, selectedPlansForUser]);

  if (loading) {
    return (
      <div className="admin-shell">
        <div className="admin-loading">Loading subscriptions...</div>
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="admin-shell">
        <div className="admin-forbidden">
          <h2 className="admin-forbidden-title">Access Denied</h2>
          <p>You do not have permission to manage user subscriptions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <div className="admin-header">
        <h1 className="admin-title">User Subscriptions</h1>
        <p className="admin-subtitle">
          Assign subscription plans to users to grant listing and offer permissions.
        </p>
        <p className="admin-subtitle">
          Note: New signups start with buyer access on the <strong>buyer_free</strong> plan by default.
        </p>
      </div>

      {(error || actionError) && (
        <div className="admin-banner admin-banner--error">{actionError || error}</div>
      )}

      <section className="admin-section">
        <h2 className="admin-section-title">Subscription Plans</h2>
        <p className="admin-section-desc">
          Available plans and the permissions they grant.
        </p>

        <div className="subscription-plan-list">
          {plans.map((plan) => (
            <div key={plan.code} className="subscription-plan-card">
              <div className="subscription-plan-card-header">
                <strong>{plan.name}</strong>
                <span className="subscription-plan-code">{plan.code}</span>
              </div>
              <div className="subscription-plan-meta">
                <span>${(plan.monthly_price_cents / 100).toFixed(2)} / month</span>
                <span>{(plan.allowed_roles || []).join(', ') || 'No roles'}</span>
              </div>
              <div className="subscription-plan-permissions">
                {(plan.permissions || []).map((permission) => (
                  <span key={permission} className="subscription-permission-pill">{permission}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="admin-section">
        <h2 className="admin-section-title">Assign Plans to Users</h2>
        <p className="admin-section-desc">
          Toggle one or more plans per user and apply changes individually.
        </p>

        <div className="admin-add-row">
          <input
            className="admin-input"
            type="search"
            placeholder="Search by name, email, or plan..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search users"
          />
        </div>

        {filteredUsers.length === 0 ? (
          <p className="admin-empty">No users match your search.</p>
        ) : (
          <div className="subscription-user-list">
            {filteredUsers.map((user) => {
              const selectedPlans = selectedPlansForUser(user);
              const hasChanges = JSON.stringify((selectedPlans || []).slice().sort())
                !== JSON.stringify((user.subscription_plans || []).slice().sort());

              const selectedPlanDetails = plans.filter((plan) => (selectedPlans || []).includes(plan.code));

              return (
                <div key={user.id} className="subscription-user-row">
                  <div className="subscription-user-main">
                    <div className="admin-user-name">
                      {user.first_name} {user.last_name}
                    </div>
                    <div className="admin-user-email">{user.email || 'No email on file'}</div>
                    <div className="subscription-user-details">
                      <PlanBadges codes={user.subscription_plans || []} />
                      <span className="admin-user-role">Legacy role: {user.legacy_role}</span>
                    </div>
                  </div>

                  <div className="subscription-user-actions">
                    <div className="subscription-toggle-list" role="group" aria-label={`Subscription plans for ${user.first_name} ${user.last_name}`}>
                      {plans.map((plan) => (
                        <button
                          key={plan.code}
                          type="button"
                          className={`subscription-toggle ${selectedPlans.includes(plan.code) ? 'is-selected' : ''}`}
                          onClick={() => onTogglePlan(user.id, plan.code, user.subscription_plans)}
                          disabled={busyUserId === user.id}
                        >
                          {plan.code}
                        </button>
                      ))}
                    </div>
                    <button
                      className="admin-btn admin-btn--add"
                      disabled={!hasChanges || busyUserId === user.id || selectedPlans.length === 0}
                      onClick={() => onAssign(user)}
                    >
                      {busyUserId === user.id ? 'Assigning...' : 'Assign'}
                    </button>
                  </div>

                  {selectedPlanDetails.length > 0 && (
                    <div className="subscription-user-permissions">
                      {[...new Set(selectedPlanDetails.flatMap((plan) => plan.permissions || []))].map((permission) => (
                        <span key={permission} className="subscription-permission-pill">
                          {permission}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
